import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LayoutDashboard, FileText, Users, Settings,
  BookOpen, LogOut, Zap, ChevronLeft, ChevronRight, X, Package
} from 'lucide-react';
import { CoinIcon } from '../ui/CoinIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [coinBalance, setCoinBalance] = useState(null);

  const fetchCoinBalance = () => {
    if (user?.role === 'guru') {
      axios.get(`${API}/coins/balance`, { withCredentials: true })
        .then(r => setCoinBalance(r.data.coin_balance))
        .catch(() => {});
    }
  };

  useEffect(() => {
    fetchCoinBalance();
    window.addEventListener('coinBalanceUpdated', fetchCoinBalance);
    return () => window.removeEventListener('coinBalanceUpdated', fetchCoinBalance);
  }, [user]);

  const navItems = {
    super_admin: [
      { to: '/dashboard',  icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/users',           icon: Users,        label: 'Manajemen Guru' },
      { to: '/subjects',        icon: BookOpen,     label: 'Mata Pelajaran' },
      { to: '/admin/packages',  icon: Package,      label: 'Paket Topup' },
      { to: '/settings',        icon: Settings,     label: 'Pengaturan' },
    ],
    guru: [
      { to: '/dashboard',        icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/documents/generate', icon: Zap,           label: 'Buat Dokumen' },
      { to: '/documents',        icon: FileText,        label: 'Dokumen Saya' },
      { to: '/topup',            icon: Zap,             label: 'Coin Guru' },
    ],
  };

  const items = navItems[user?.role] || [];
  const showCoin = user?.role === 'guru';

  // Tier warna berdasarkan saldo koin
  const coinTier = coinBalance === null ? 'normal'
    : coinBalance <= 5  ? 'critical'   // merah
    : coinBalance <= 20 ? 'warning'    // kuning
    : 'normal';                        // hijau

  const coinStyle = {
    critical: {
      card:   'bg-red-900/40 border border-red-700/50',
      number: 'text-red-300',
      label:  'text-red-400',
      badge:  <span className="text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded-full font-bold">Kritis!</span>,
      tap:    '⚠ Tap untuk isi ulang →',
    },
    warning: {
      card:   'bg-yellow-900/30 border border-yellow-700/40',
      number: 'text-yellow-300',
      label:  'text-yellow-500',
      badge:  <span className="text-[10px] bg-yellow-500 text-white px-1.5 py-0.5 rounded-full font-bold">Menipis!</span>,
      tap:    '⚠ Tap untuk isi ulang →',
    },
    normal: {
      card:   'bg-green-900/30 border border-green-700/40',
      number: 'text-green-300',
      label:  'text-green-500',
      badge:  null,
      tap:    'Tap untuk isi ulang →',
    },
  }[coinTier];

  const sidebarContent = (
    <div className="flex flex-col h-full" style={{ background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)' }}>

      {/* Logo + collapse toggle */}
      <div className={`flex items-center border-b border-slate-700/50 flex-shrink-0 ${collapsed ? 'justify-center p-4' : 'justify-between p-5'}`}>
        {!collapsed && (
          <div className="flex items-center gap-2.5">
            <img src="/logo-white.png" alt="RencanaGuru" className="h-8 w-8 object-contain flex-shrink-0" />
            <div>
              <div className="font-bold text-white text-sm leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                RencanaGuru<span className="text-yellow-400">.com</span>
              </div>
              <div className="text-slate-400 text-xs">Platform AI Guru</div>
            </div>
          </div>
        )}
        {collapsed && <img src="/logo-white.png" alt="RencanaGuru" className="h-8 w-8 object-contain" />}

        <button
          onClick={onToggle}
          className="hidden lg:flex w-7 h-7 rounded-lg bg-slate-700/50 hover:bg-slate-600 items-center justify-center text-slate-400 hover:text-white transition-colors flex-shrink-0"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        <button
          onClick={onMobileClose}
          className="lg:hidden w-7 h-7 rounded-lg bg-slate-700/50 hover:bg-slate-600 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {/* User info */}
      {!collapsed && (
        <div className="mx-4 mt-4 flex-shrink-0">
          <div className="flex items-center gap-2.5 bg-slate-800/60 rounded-xl p-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0">
              <div className="text-white text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-slate-400 text-xs">
                {user?.role === 'guru' ? 'Guru' : 'Super Admin'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coin Guru balance banner */}
      {showCoin && !collapsed && (
        <button
          onClick={() => { navigate('/topup'); onMobileClose?.(); }}
          className={`mx-4 mt-3 rounded-xl p-3.5 flex-shrink-0 text-left transition-all hover:scale-[1.02] ${coinStyle.card}`}
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              <CoinIcon size={20} />
              <span className="text-xs font-semibold text-white">Coin Guru</span>
            </div>
            {coinStyle.badge}
          </div>
          <div className={`text-2xl font-bold ${coinStyle.number}`}>
            {coinBalance ?? '...'} <span className="text-sm font-normal opacity-70">koin</span>
          </div>
          <div className={`text-[11px] mt-1 font-semibold ${coinStyle.label}`}>
            {coinStyle.tap}
          </div>
        </button>
      )}

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto p-3 mt-2 space-y-1 min-h-0">
        {items.map((item) => {
          const Icon = item.icon;
          const isCoin = item.to === '/topup';
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end
              onClick={onMobileClose}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl transition-all text-sm ${
                  collapsed ? 'justify-center px-3 py-3' : 'px-4 py-2.5'
                } ${isActive
                    ? 'text-white font-semibold shadow-lg'
                    : isCoin && coinTier !== 'normal'
                      ? 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`
              }
              style={({ isActive }) => isActive ? { background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' } : {}}
            >
              {isCoin
                ? <CoinIcon size={20} className="flex-shrink-0" />
                : <Icon size={18} className="flex-shrink-0" />
              }
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && isCoin && coinBalance !== null && (
                <span className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full ${
                  coinTier === 'critical' ? 'bg-red-500/30 text-red-300'
                  : coinTier === 'warning' ? 'bg-yellow-500/20 text-yellow-400'
                  : 'bg-green-500/20 text-green-400'
                }`}>
                  {coinBalance}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-slate-700/50 flex-shrink-0">
        <button
          onClick={() => { logout(); onMobileClose?.(); }}
          data-testid="logout-button"
          title={collapsed ? 'Keluar' : undefined}
          className={`flex items-center gap-3 rounded-xl w-full text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all text-sm ${
            collapsed ? 'justify-center px-3 py-3' : 'px-4 py-2.5'
          }`}
        >
          <LogOut size={18} className="flex-shrink-0" />
          {!collapsed && <span>Keluar</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className={`hidden lg:flex flex-col h-screen sticky top-0 flex-shrink-0 transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`}>
        {sidebarContent}
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onMobileClose} />
          <div className="relative z-50 w-72 h-full flex flex-col">{sidebarContent}</div>
        </div>
      )}
    </>
  );
};
